import ActionCard from "components/shared/ActionCard";
import TitleSeparation from "components/shared/TitleSeparation";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import { fetchExportedFiles } from "store/filesHistorySlice";
import { useAppDispatch, useAppSelector } from "store/store";

import styles from "../style.module.scss";
import type { DeclarationsCardsData } from "../utils";

interface IDeclarationsHistory {
    /**
     * History declarations data.
     */
    cards: DeclarationsCardsData[];
    /**
     * ID of last declaration set as done (needed for the color animation).
     */
    lastDeclarationDone: number | undefined;
    setLastDeclarationDone: Dispatch<SetStateAction<number | undefined>>;
}

/**
 * Within Declarations Page, section that includes only declarations that were done and saved.
 */
const DeclarationsHistory = ({ cards, lastDeclarationDone, setLastDeclarationDone }: IDeclarationsHistory) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const cardRef = useRef(null);

    const lastDeclaration = cards.find((item) => item.id === lastDeclarationDone);
    const oldDeclarations = cards.filter((item) => item.id !== lastDeclarationDone);

    const { exportedFiles, exportedFilesStatus } = useAppSelector((state) => state.filesHistory);

    /**
     * All exported files concerning the DAS2.
     */
    const exportedDeclarationFiles = useMemo(() => {
        return exportedFiles.filter((item) => item.target === "DAS2" || item.target === "URSSAF");
    }, [exportedFiles]);

    useEffect(() => {
        if (exportedFilesStatus === "idle") {
            dispatch(fetchExportedFiles({ options: { target: ["URSSAF", "DAS2"], limit: undefined } }));
        }
    }, [exportedFilesStatus]);

    return (
        <div className={styles["declarations-history"]}>
            <TitleSeparation title={t("Declarations.History")} />
            {cards.length > 0 ? (
                <div className={styles["cards-container"]}>
                    <CSSTransition
                        nodeRef={cardRef}
                        in={!!lastDeclaration}
                        classNames={"success-gradient"}
                        timeout={1000}
                    >
                        {lastDeclaration ? (
                            <ActionCard
                                {...lastDeclaration}
                                isCompact
                                squareBtn
                                key={`history-card-${lastDeclaration?.id}}`}
                                ref={cardRef}
                                gradientAnimation
                            />
                        ) : (
                            <></>
                        )}
                    </CSSTransition>
                    {oldDeclarations.map((card) => {
                        return <ActionCard {...card} isCompact squareBtn key={`history-card-${card.id}}`} />;
                    })}
                </div>
            ) : (
                <span className={styles["no-history-info"]}>{t("Declarations.NoHistory")}</span>
            )}
            {exportedDeclarationFiles.length > 0 ? (
                <span className={styles["old-history-info"]}>{t("Declarations.ImportHistory")}</span>
            ) : (
                <></>
            )}
        </div>
    );
};

export default DeclarationsHistory;
